import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";

// Required advance payment is 13.5% of the indicative drive-away price.
const DEPOSIT_PERCENT = 0.135;
const TERM_MULTIPLIER = { 1: 1.25, 2: 1.55, 3: 1.82 };
const CYCLES = {
  Weekly: 52,
  Fortnightly: 26,
  Monthly: 12,
};

const fmt = (n) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

export default function Calculator() {
  const [make, setMake] = useState("Toyota");
  const [model, setModel] = useState("Corolla Hybrid");
  const [year, setYear] = useState(2026);
  const [price, setPrice] = useState(35000);
  const [cycle, setCycle] = useState("Weekly");
  const [years, setYears] = useState(3);
  const [showResult, setShowResult] = useState(false);

  // 13.5% is the required minimum. User may increase it if they wish.
  const minDeposit = Math.round(price * DEPOSIT_PERCENT);
  const [deposit, setDeposit] = useState(minDeposit);

  // Keep deposit >= 13.5% whenever price rises above the current deposit.
  useEffect(() => {
    setDeposit((d) => (d < minDeposit ? minDeposit : d));
  }, [minDeposit]);

  const calc = useMemo(() => {
    const financed = Math.max(0, price - deposit);
    const total = financed * (TERM_MULTIPLIER[years] || 1.82);
    const periods = (CYCLES[cycle] || 52) * years;
    const payment = total / periods;
    return { financed, total, payment, periods };
  }, [price, deposit, cycle, years]);

  const depositPercent = price > 0 ? ((deposit / price) * 100).toFixed(1) : "0.0";

  const onCalculate = (e) => {
    e.preventDefault();
    if (deposit < minDeposit) {
      toast.error(`Advance payment must be at least 13.5% (${fmt(minDeposit)}).`);
      return;
    }
    setShowResult(true);
  };

  const onReset = () => {
    setMake("Toyota");
    setModel("Corolla Hybrid");
    setYear(2026);
    setPrice(35000);
    setDeposit(Math.round(35000 * DEPOSIT_PERCENT));
    setCycle("Weekly");
    setYears(3);
    setShowResult(false);
  };

  return (
    <div
      data-testid="payment-calculator"
      className="bg-[#0E0E0E] border border-white/10 rounded-2xl p-6 md:p-10 shadow-[0_30px_120px_-30px_rgba(212,175,55,0.25)]"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="h-px w-10 bg-[#D4AF37]" />
        <span className="label-eyebrow">Repayment Plan</span>
      </div>
      <h3 className="font-display text-3xl md:text-4xl text-white mb-1" style={{ fontWeight: 400 }}>
        Car Payment Calculator
      </h3>
      <p className="text-sm text-white/50 mb-8">
        Estimate your indicative rent-to-own repayments. Final amounts depend on vehicle and approval.
      </p>

      <form onSubmit={onCalculate} className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Make">
          <input
            data-testid="calc-make"
            type="text"
            value={make}
            onChange={(e) => setMake(e.target.value)}
            className={inputCls}
          />
        </Field>

        <Field label="Model">
          <input
            data-testid="calc-model"
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className={inputCls}
          />
        </Field>

        <Field label="Year">
          <select
            data-testid="calc-year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className={inputCls}
          >
            {[2026, 2025, 2024].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Indicative Drive Away Price (AUD)">
          <input
            data-testid="calc-price"
            type="number"
            min="5000"
            step="1"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            className={inputCls}
          />
        </Field>

        <Field label="Advance Payment (minimum 13.5% of price)">
          <div className="relative">
            <input
              data-testid="calc-deposit"
              type="number"
              min={minDeposit}
              step="1"
              value={deposit}
              onChange={(e) => setDeposit(Number(e.target.value) || 0)}
              className={`${inputCls} pr-20`}
            />
            <span
              data-testid="calc-deposit-percent"
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${
                deposit >= minDeposit
                  ? "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30"
                  : "text-red-300 bg-red-500/10 border-red-500/30"
              }`}
            >
              {depositPercent}%
            </span>
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[11px]">
            <span className="text-white/40">
              Minimum <span className="text-[#D4AF37]">{fmt(minDeposit)}</span> (13.5%)
            </span>
            {deposit > minDeposit && (
              <button
                type="button"
                data-testid="calc-deposit-reset"
                onClick={() => setDeposit(minDeposit)}
                className="text-white/50 hover:text-[#D4AF37] transition-colors underline underline-offset-2"
              >
                Reset to minimum
              </button>
            )}
          </div>
        </Field>

        <Field label="Payment Cycle">
          <select
            data-testid="calc-cycle"
            value={cycle}
            onChange={(e) => setCycle(e.target.value)}
            className={inputCls}
          >
            <option>Weekly</option>
            <option>Fortnightly</option>
            <option>Monthly</option>
          </select>
        </Field>

        <Field label="Number of Years">
          <div className="flex gap-2">
            {[1, 2, 3].map((y) => (
              <button
                key={y}
                type="button"
                data-testid={`calc-years-${y}`}
                onClick={() => setYears(y)}
                className={`flex-1 py-3 text-sm tracking-wide border transition-all ${
                  years === y
                    ? "bg-[#D4AF37] border-[#D4AF37] text-black"
                    : "border-white/15 text-white/70 hover:border-[#D4AF37]/60 hover:text-white"
                }`}
              >
                {y} {y === 1 ? "Year" : "Years"}
              </button>
            ))}
          </div>
        </Field>

        <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 mt-2">
          <button
            type="submit"
            data-testid="calc-submit"
            className="flex-1 bg-[#D4AF37] text-black font-medium py-4 px-6 hover:bg-[#F6ECCC] transition-colors tracking-wide"
          >
            Calculate Payment
          </button>
          <button
            type="button"
            data-testid="calc-reset"
            onClick={onReset}
            className="sm:w-40 border border-white/15 text-white/80 py-4 px-6 hover:border-white/40 transition-colors"
          >
            Reset
          </button>
        </div>
      </form>

      {showResult && (
        <div
          data-testid="calc-result"
          className="mt-10 border border-[#D4AF37]/30 bg-gradient-to-br from-[#0a0a0a] to-[#141004] rounded-xl p-8 fade-up"
        >
          <div className="label-eyebrow mb-2">Estimated Repayments</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-5xl md:text-6xl text-white" style={{ fontWeight: 300 }}>
              {fmt(calc.payment)}
            </span>
            <span className="text-white/60">
              / {cycle.toLowerCase()} for {years} {years === 1 ? "year" : "years"}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-white/10">
            <Stat label="Vehicle" value={`${make} ${model}`} mono />
            <Stat label="Financed" value={fmt(calc.financed)} />
            <Stat label="Total Repayments" value={fmt(calc.total)} />
            <Stat label="Total Periods" value={String(calc.periods)} />
          </div>

          <p className="text-xs text-white/40 mt-6 leading-relaxed">
            * Indicative only. Subject to approval, vehicle availability and final Operating
            Lease Agreement terms. Final repayment schedule will be confirmed in writing.
          </p>
        </div>
      )}
    </div>
  );
}

const inputCls =
  "w-full bg-[#0a0a0a] border border-white/10 text-white px-4 py-3 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1">{label}</div>
      <div className={`text-white ${mono ? "text-sm" : "text-lg"} font-medium`}>{value}</div>
    </div>
  );
}
